import * as Sentry from '@sentry/browser';

import type { FailEvent } from '@atlassian/experience-tracker/ExperienceEvent';

import type { MonitoringClient, MonitoringErrorOptions, MonitoringMessageOptions } from './types';
import { getCustomAttributes, hasGraphQlProperties, isApolloError } from './util';

export class ProductionMonitoringClient implements MonitoringClient {
	onError(error: unknown, options?: MonitoringErrorOptions): void {
		if (hasGraphQlProperties(error)) {
			Sentry.withScope((scope) => {
				scope.setTag('isGraphQLError', true);
				scope.setTag('graphQLErrorOperation', error?.operation);
				scope.setTag('graphqlErrorPath', error?.path?.join(','));
				scope.setFingerprint(options?.fingerprint ?? [error?.operation]);

				const newMessage = `GraphQL Error - operation:${error?.operation} - message:${error?.message}`;
				const newError = new Error(newMessage);

				Sentry.captureException(newError);
			});

			return;
		}

		Sentry.withScope((scope) => {
			if (options?.fingerprint) {
				scope.setFingerprint(options.fingerprint);
			}

			if (!(error instanceof Error)) {
				let nonErrorWrapper: Error | undefined;

				try {
					nonErrorWrapper = new Error(`Non-Error rejection: ${JSON.stringify(error)}`);
				} catch {
					// rare case of Big Int or Circular object
					nonErrorWrapper = new Error(`Non-Error rejection: ${error}. Unserializable object.`);
				}

				Sentry.captureException(nonErrorWrapper);

				return;
			}

			Sentry.captureException(error);
		});
	}

	onMessage(message: string, options?: MonitoringMessageOptions): void {
		Sentry.withScope((scope) => {
			if (options?.level) {
				scope.setLevel(options.level);
			}

			if (options?.tags) {
				scope.setTags(options.tags);
			}

			if (options?.contexts) {
				Object.entries(options.contexts).forEach(([name, context]) => {
					scope.setContext(name, context);
				});
			}

			Sentry.captureMessage(message);
		});
	}

	onUIError(error: unknown): void {
		if (isApolloError(error)) {
			return;
		}

		this.onError(error);
	}

	onExperienceFailure(event: FailEvent): string {
		return Sentry.captureException(event.error, {
			tags: {
				experience: event.name,
			},
			contexts: {
				experience: {
					id: event.id,
					...getCustomAttributes(event, ['compoundTaskId']),
				},
			},
		});
	}
}
